import { Button } from "@/components/Button";
import { Calendar, Filter, X } from "lucide-react";
import { useState } from "react";
import { router } from '@inertiajs/react';
import { cn } from "@/lib/utils";

export function FilterBar({ currentFilters = {}, reportPath = "/admin/reports/redemptions", additionalFilters = [] }) {
  const [range, setRange] = useState(currentFilters.range || "last_30_days");
  const [category, setCategory] = useState(currentFilters.category || "");
  const [status, setStatus] = useState(currentFilters.status || "");
  const [customDateRange, setCustomDateRange] = useState({
    start_date: currentFilters.start_date || '',
    end_date: currentFilters.end_date || ''
  });

  const hasActiveFilters = category !== "" || status !== "";

  const handleApplyFilters = () => {
    const params = {};
    if (range) params.range = range;
    if (range === 'custom') {
      params.start_date = customDateRange.start_date;
      params.end_date = customDateRange.end_date;
    }
    if (category) params.category = category;
    if (status) params.status = status;

    // Copiar filtros adicionales desde currentFilters
    additionalFilters.forEach(filterKey => {
      if (currentFilters[filterKey]) {
        params[filterKey] = currentFilters[filterKey];
      }
    });

    router.visit(reportPath, {
      data: params,
      preserveState: true
    });
  };

  const handleApplyCustomDates = () => {
    if (customDateRange.start_date && customDateRange.end_date) {
      const params = {
        range: 'custom',
        start_date: customDateRange.start_date,
        end_date: customDateRange.end_date
      };
      if (category) params.category = category;
      if (status) params.status = status;

      additionalFilters.forEach(filterKey => {
        if (currentFilters[filterKey]) {
          params[filterKey] = currentFilters[filterKey];
        }
      });

      router.visit(reportPath, { data: params });
    }
  };

  const handleClearFilters = () => {
    setRange("last_30_days");
    setCategory("");
    setStatus("");
    setCustomDateRange({ start_date: '', end_date: '' });

    router.visit(reportPath, {
      data: { range: "last_30_days" },
      preserveState: true
    });
  };

  const dateRanges = [
    { value: "last_7_days", label: "Últimos 7 días" },
    { value: "last_30_days", label: "Últimos 30 días" },
    { value: "last_90_days", label: "Últimos 90 días" },
    { value: "current_month", label: "Mes actual" },
    { value: "current_year", label: "Año actual" },
    { value: "custom", label: "Rango personalizado..." }
  ];

  const categories = [
    { value: "", label: "Todas las categorías" },
    { value: "cash", label: "💰 Saldo" },
    { value: "experiences", label: "🎉 Experiencias" },
    { value: "charity", label: "❤️ Caridad" }
  ];

  const statuses = [
    { value: "", label: "Todos los estados" },
    { value: "pending", label: "Pendiente" },
    { value: "approved", label: "Aprobada" },
    { value: "fulfilled", label: "Cumplida" },
    { value: "rejected", label: "Rechazada" },
    { value: "cancelled", label: "Cancelada" }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-bold text-gray-700 uppercase">Filtros</h3>
      </div>

      <div className={cn(
        "grid grid-cols-1 gap-4 items-end",
        hasActiveFilters ? "md:grid-cols-5" : "md:grid-cols-4"
      )}>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-2 block flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Rango de Fechas
          </label>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            {dateRanges.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600 mb-2 block">
            Categoría
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            {categories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600 mb-2 block">
            Estado
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            {statuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            Aplicar Filtros
          </Button>
        </div>

        {hasActiveFilters && (
          <div>
            <Button
              onClick={handleClearFilters}
              variant="outline"
              className="w-full border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          </div>
        )}
      </div>

      {/* Rango de fechas personalizado */}
      {range === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Fecha Inicio</label>
            <input
              type="date"
              value={customDateRange.start_date}
              onChange={(e) => setCustomDateRange({ ...customDateRange, start_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Fecha Fin</label>
            <input
              type="date"
              value={customDateRange.end_date}
              onChange={(e) => setCustomDateRange({ ...customDateRange, end_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleApplyCustomDates}
              disabled={!customDateRange.start_date || !customDateRange.end_date}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aplicar Fechas
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
